"use client";

import dynamic from "next/dynamic";
import type { ComponentProps } from "react";
import { videoBoxStyle } from "@/lib/lecturehub/video";
import type { VideoPlayer as VideoPlayerType } from "./VideoPlayer";

/**
 * SSR-safe entry point for the video player. The real `<VideoPlayer />`
 * loads the external lhubPlayer bundle at runtime (attaches to `window`),
 * so it must never render on the server. Every caller imports this
 * wrapper instead of VideoPlayer directly.
 */
const VideoPlayer = dynamic(() => import("./VideoPlayer").then((m) => m.VideoPlayer), {
  ssr: false,
  loading: () => (
    <div
      aria-label="Loading player"
      role="status"
      className="h-full w-full bg-foreground/90 rounded-2xl grid place-items-center"
    >
      <div className="size-8 border-2 border-background/30 border-t-background/80 rounded-full animate-spin" />
    </div>
  ),
});

export function DynamicVideoPlayer(props: ComponentProps<typeof VideoPlayerType>) {
  // `loading` cannot see the props, so the box is reserved out here from the
  // video's own shape (`width`/`height`, 16:9 when unknown): the spinner fills
  // it and the player takes it over without a jump. Before, a portrait stream
  // loaded into a 16:9 box and then grew past it under the callers'
  // `overflow-hidden`.
  return (
    <div style={videoBoxStyle(props)} className="grid w-full">
      <VideoPlayer {...props} />
    </div>
  );
}
